import { Header } from "./-components/header"
import { useEffect } from "react"
import { createFileRoute } from "@tanstack/react-router"

export const Route = createFileRoute("/(site)/terms")({
  component: TermsAndConditions,
})

function TermsAndConditions() {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-24 max-w-4xl">
        <article className="prose prose-slate dark:prose-invert max-w-none">
          <h1>
            Terms & Conditions for the Knowledge Facts Ladder (KFL) Online
            Quizzing League
          </h1>
          <p className="text-muted-foreground">Last updated: 03.12.2025</p>

          <p>
            These Terms & Conditions ("Terms") govern access to and use of the
            Knowledge Facts Ladder ("KFL", "we", "our", "the League") website,
            quizzes, events, and related services. By creating an account or
            participating in any KFL activity, you agree to be bound by these
            Terms.
          </p>
          <p>
            If you do not agree, you must not use the website or participate in
            KFL events.
          </p>

          <h2>1. Legal Entity and Jurisdiction</h2>
          <p>
            1.1. KFL is operated by an individual entrepreneur registered in
            Ukraine.
          </p>
          <p>1.2. Legal jurisdiction: Ukraine.</p>
          <p>
            1.3. Official business address: Ukraine, Kharkiv, Pavla Tychyny 23,
            61009.
          </p>

          <h2>2. Eligibility</h2>
          <p>
            2.1. Participants must be at least 16 years old or the age of
            digital consent applicable in their country.
          </p>
          <p>
            2.2. You confirm that all information you provide is accurate and
            that you have legal capacity to enter into these Terms.
          </p>
          <p>
            2.3. KFL reserves the right to deny access or remove accounts for
            misuse, fraud, or any breach of these Terms.
          </p>
          <p>
            2.4. KFL reserves the right to refuse access or deny participation
            to any player if there is credible evidence or reasonable belief
            that the player has previously engaged in cheating, harassment,
            toxic behavior, or other inappropriate conduct — whether within KFL
            or in any other quiz league or competitive environment. KFL is not
            required to provide explanation or evidence for such decisions. All
            such decisions are final.
          </p>

          <h2>3. Accounts</h2>
          <p>
            3.1. A KFL account is required to access quizzes, track results, or
            use subscription features.
          </p>
          <p>
            3.2. You are responsible for keeping your login credentials secure.
          </p>
          <p>
            3.3. Multiple accounts ("multiaccounts") are strictly prohibited.
            Violations may result in temporary or permanent bans.
          </p>
          <p>
            3.4. You may not allow another person to participate using your
            account.
          </p>
          <p>
            3.5. KFL may suspend or terminate accounts suspected of unauthorized
            use or deception.
          </p>

          <h2>4. Subscriptions and Payments</h2>
          <p>
            4.1. KFL offers tiered access and participation based on
            subscription levels.
          </p>
          <p>
            4.2. Accepted payment methods include: PayPal, Wise, Payoneer, and
            Monobank online banking (preferred method).
          </p>
          <p>4.3. Subscription fees are charged upon purchase.</p>
          <p>
            4.4. Subscriptions renew automatically where applicable unless
            cancelled before renewal.
          </p>
          <p>
            4.5. Payments are non-refundable except for confirmed double
            charges, which will be refunded.
          </p>
          <p>
            4.6. If a dispute or chargeback is initiated through a payment
            provider, the user's account may be suspended pending resolution.
          </p>

          <h2>5. Credits and Pay-Per-Game Options</h2>
          <p>
            5.1. Some events may allow players to deposit credit and pay per
            quiz or per session.
          </p>
          <p>
            5.2. Unused credit is non-refundable but remains available as long
            as the account is active.
          </p>
          <p>
            5.3. KFL is not responsible for unused balance if the player stops
            participating.
          </p>

          <h2>6. Participation Rules</h2>
          <p>
            6.1. Players must participate fairly and without outside assistance.
          </p>
          <p>6.2. Prohibited actions include (but are not limited to):</p>
          <ul>
            <li>a) using search engines, notes, or external materials;</li>
            <li>b) collaborating during individual competitions;</li>
            <li>c) using AI assistance of any kind;</li>
            <li>d) pausing, delaying, or bypassing time limits;</li>
            <li>e) using multiple accounts;</li>
            <li>f) manipulating or falsifying recorded material.</li>
          </ul>
          <p>
            6.3. Sharing screenshots or personal score sheets is permitted only
            if it does not compromise competitive fairness.
          </p>
          <p>
            6.4. Any attempt to gain an unfair advantage may result in
            disqualification or a permanent ban.
          </p>

          <h2>7. Recording, Cameras, and Publication</h2>
          <p>
            7.1. Certain KFL events may be recorded for integrity and fairness
            purposes.
          </p>
          <p>
            7.2. Recordings are created primarily for anti-cheating measures,
            including verification of fair play, investigation of
            irregularities, and dispute resolution.
          </p>
          <p>
            7.3. During Pairs and Teams events, players are required to keep
            their camera turned on for the duration of the game.
          </p>
          <p>
            7.4. Raw recordings may be stored by KFL for up to 12 months if they
            are not published.
          </p>
          <p>
            7.5. By participating, you grant KFL permission to use recordings —
            including your gameplay, voice, video, username, and visible
            interactions — for promotional, educational, archival, or monetized
            content (e.g., YouTube uploads) without additional notice or
            compensation.
          </p>
          <p>
            7.6. If you do not agree to being recorded or published, you must
            not participate in KFL events.
          </p>
          <p>
            7.7. KFL is not responsible for recordings made independently by
            other players.
          </p>

          <h2>8. Quiz Content and Intellectual Property</h2>
          <p>
            8.1. All quiz questions, answers, formats, graphics, images,
            recordings, and related materials are the intellectual property of
            KFL or its licensors.
          </p>
          <p>
            8.2. The following are strictly prohibited without written
            permission from KFL:
          </p>
          <ul>
            <li>
              a) reposting or publishing quiz questions in any form, including
              Discord or social media;
            </li>
            <li>
              b) using KFL materials in other leagues, competitions, or events;
            </li>
            <li>
              c) sharing, uploading, or distributing proprietary files (PDFs,
              question sets, answer sheets, databases);
            </li>
            <li>d) using KFL content to train or fine-tune AI models;</li>
            <li>e) redistributing raw video or session recordings.</li>
          </ul>
          <p>
            8.3. Screenshots or personal score snapshots may be shared only if
            they do not reveal protected question content.
          </p>
          <p>
            8.4. Unauthorized use of KFL intellectual property may result in a
            permanent ban and possible legal action.
          </p>

          <h2>9. Events and Scoring</h2>
          <p>
            9.1. KFL hosts multiple event formats, including daily circuits,
            specialist quizzes, pairs, teams, and special tournaments.
          </p>
          <p>
            9.2. KFL may adjust scoring, difficulty, question count, or event
            format at any time.
          </p>
          <p>
            9.3. KFL is not responsible for player-side technical failures such
            as poor internet connection or device malfunctions.
          </p>
          <p>9.4. KFL does not offer cash prizes.</p>
          <p>9.5. All scoring decisions by KFL are final.</p>

          <h2>10. Leaderboards and Public Results</h2>
          <p>
            10.1. By participating, you consent to your name, country, and
            scores appearing on public leaderboards.
          </p>
          <p>
            10.2. You may request the use of a pseudonym for leaderboard
            display. By default, pseudonyms are prohibited.
          </p>

          <h2>11. Conduct and Community Rules</h2>
          <p>
            11.1. Players must behave respectfully in all KFL-related spaces,
            including chats, social communities, and team communication
            channels.
          </p>
          <p>
            11.2. Toxic behavior, harassment, threats, hate speech, or
            disruptive conduct results in immediate ban.
          </p>
          <p>
            11.3. Ban durations may be temporary or permanent depending on
            circumstances.
          </p>
          <p>
            11.4. All banning or disqualification decisions made by KFL
            administration are final and cannot be appealed.
          </p>
          <p>
            11.5. KFL may refuse entry or remove players based on prior
            misconduct or cheating incidents from other leagues or communities.
            These decisions are made at the sole discretion of KFL
            administration and are not subject to appeal.
          </p>

          <h2>12. Website Use</h2>
          <p>
            12.1. Users must not attempt to disrupt, reverse-engineer, or
            exploit the KFL website or systems.
          </p>
          <p>
            12.2. KFL may update or modify the website at any time, including
            adding or removing features.
          </p>
          <p>
            12.3. Users must not attempt to bypass access controls or
            subscription restrictions.
          </p>

          <h2>13. Data Protection and Privacy</h2>
          <p>
            13.1. KFL collects data necessary for account management, event
            participation, scoring, payment processing, and anti-cheating
            measures.
          </p>
          <p>
            13.2. Detailed information is provided in the{" "}
            <a href="/privacy">KFL Privacy Policy</a>.
          </p>
          <p>
            13.3. You may request deletion of your personal data; however, this
            may permanently remove your account and results.
          </p>
          <p>
            13.4. For security reasons, KFL does not publicly disclose data
            storage infrastructure.
          </p>

          <h2>14. Disclaimers</h2>
          <p>
            14.1. KFL provides all services "as is" without guarantees of
            uninterrupted access.
          </p>
          <p>
            14.2. KFL accepts no liability for technical interruptions or
            service outages involving third-party platforms.
          </p>
          <p>14.3. Participation is at your own risk.</p>

          <h2>15. Limitation of Liability</h2>
          <p>To the fullest extent permitted by law, KFL is not liable for:</p>
          <ul>
            <li>a) indirect or consequential damages;</li>
            <li>b) loss of earnings or opportunities;</li>
            <li>c) technical delays or downtime;</li>
            <li>d) delayed leaderboard updates;</li>
            <li>e) results impacted by player-side technical issues.</li>
          </ul>
          <p>
            KFL's total liability is limited to the amount you paid to KFL
            within the preceding 12 months.
          </p>

          <h2>16. Changes to the Terms</h2>
          <p>16.1. KFL may revise these Terms at any time.</p>
          <p>
            16.2. Significant changes will be announced via the website or
            email.
          </p>
          <p>
            16.3. Continued use of KFL services constitutes acceptance of
            updated Terms.
          </p>

          <h2>17. Contact</h2>
          <p>For support, questions, or account issues, contact:</p>
          <p>
            <a href="mailto:knowledge.facts.ladder@gmail.com">
              knowledge.facts.ladder@gmail.com
            </a>
            <br />
            <a href="https://www.kflquiz.com">www.kflquiz.com</a>
          </p>
        </article>
      </main>
    </div>
  )
}
